import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  Root as DialogRoot,
  DialogTrigger,
} from "@radix-ui/react-dialog";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import clsx from "clsx";
import { PaymentMethod } from "constants/methods";
import { AnimatePresence, motion } from "framer-motion";
import {
  gradientRowBgClass,
  referralCtaButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import {
  AnimatedGradientBorder,
  GlowCard,
} from "screens/Landing/components/shared";
import { useTranslation } from "react-i18next";
import SubscribeBlock from "./SubscribeBlock";
import SocialIcons from "../LandingFooter/components/SocialIcons";

interface PaymentResultModalProps {
  isOpen: boolean;
  onOpenChange: (val: boolean) => void;
  isSuccess: boolean;
  amount: number;
  currency: string;
  walletAddress: string;
  paymentMethod: PaymentMethod;
}

export function PaymentResultModal({
  isOpen,
  onOpenChange,
  isSuccess,
  amount,
}: PaymentResultModalProps) {
  const { t } = useTranslation();

  const headerText = isSuccess
    ? t("paymentResult.successHeader")
    : t("paymentResult.failedHeader");
  const resultMessage = isSuccess
    ? t("paymentResult.successMessage", {
        amount: amount.toFixed(2) || "0.00",
      })
    : t("paymentResult.failedMessage");
  const finalMessage = isSuccess
    ? t("paymentResult.successFinalMessage")
    : t("paymentResult.failedFinalMessage");

  const messageTint = isSuccess
    ? "border-emerald-500/25 bg-emerald-500/10 text-white/90"
    : "border-red-500/30 bg-red-500/10 text-white/90";

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {isOpen && (
          <DialogPortal forceMount>
            <DialogOverlay asChild>
              <motion.div className="fixed inset-0 z-[499] bg-black/50" />
            </DialogOverlay>
            <DialogContent
              className="!fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[499] w-[445px] max-lg:w-[360px] max-lg:max-h-[90vh] h-auto rounded-[28px] max-lg:rounded-[28px] outline-none"
            >
              <AnimatedGradientBorder
                className="rounded-[28px] max-lg:rounded-[28px] w-full"
              >
                <GlowCard className="flex flex-col h-full max-lg:rounded-[28px] max-lg:before:!rounded-[28px] rounded-[28px] before:!rounded-[28px] bg-[#111111] overflow-hidden p-[12px] max-lg:p-[12px] ring-1 ring-inset ring-white/[0.06]">
                  <motion.div
                    className={clsx(
                      "flex justify-between items-center max-lg:rounded-[16px] max-lg:before:!rounded-[15px] rounded-[16px] before:!rounded-[16px] p-[8px] max-lg:p-[9px] max-lg:pl-[12px] pl-[16px]",
                      gradientRowBgClass,
                    )}
                  >
                    <h2 className="text-white font-luxora-medium text-18 max-lg:text-20m">
                      {headerText}
                    </h2>
                  </motion.div>

                  <div
                    className={clsx(
                      "flex flex-col items-center rounded-[16px] mt-[15px] max-lg:rounded-[12px] gap-[8px] max-lg:gap-[8px] before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-[#161616] p-[8px] max-lg:p-[8px] w-full",
                      shadowAndBorderClass,
                    )}
                  >
                    <div
                      className={clsx(
                        "w-[52px] h-[52px] flex items-center justify-center max-lg:w-[52px] max-lg:h-[52px] bg-[#252525] rounded-full before:!rounded-full",
                        shadowAndBorderClass,
                      )}
                    >
                      <AttsIcon className="w-[55px] h-[55px] max-lg:w-[52px] max-lg:h-[52px]" />
                    </div>

                    <div className="text-16 max-lg:text-16m font-luxora-medium text-white leading-[110%]">
                      {amount ? amount.toFixed(2) : 0}{" "}
                      <span className="text-16 max-lg:text-16m text-white/40 leading-[110%]">
                        $ATTS
                      </span>
                    </div>
                  </div>

                  <div className="overflow-auto">
                    <div
                      className={clsx(
                        "text-[16px] font-luxora-regular leading-[110%] max-lg:p-[12px] max-lg:rounded-[12px] max-lg:text-16m p-[12px] border-[2px] text-center",
                        "rounded-[16px] border-white/[0.08] max-lg:my-[12px] mt-[12px] mb-[24px]",
                        messageTint,
                      )}
                    >
                      {resultMessage}
                      <br />
                      <span className="mt-[7px] block text-white/70">
                        {finalMessage}
                      </span>
                    </div>
                    <div
                      className={clsx(
                        "flex flex-col lg:text-sm sm:text-[16px] items-center rounded-[16px] max-lg:rounded-[12px] gap-[8px] max-lg:gap-[8px] before:!rounded-[16px] max-lg:before:!rounded-[12px] bg-[#161616] p-[8px] max-lg:p-[8px] w-full text-center text-white/80 font-luxora-regular",
                        shadowAndBorderClass,
                      )}
                    >
                      Join our social media for updates!
                      <SocialIcons variant="dark" />
                    </div>
                    <div className="my-[20px]">
                      <SubscribeBlock />
                    </div>
                    <DialogTrigger asChild>
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        type="button"
                        className={clsx(
                          referralCtaButtonClass,
                          "relative w-full !justify-center font-luxora-medium text-black max-lg:text-16m text-16",
                        )}
                      >
                        {t("paymentResult.gotIt")}
                      </motion.button>
                    </DialogTrigger>
                  </div>
                </GlowCard>
              </AnimatedGradientBorder>
            </DialogContent>
          </DialogPortal>
        )}
      </AnimatePresence>
    </DialogRoot>
  );
}
